// Instruction builders for the two container instructions: init_container and
// append_version. Mirrors the two builders in append.ts — `.rpc()` directly,
// errors left exactly as Anchor throws them so `humanizeForumError` (which
// walks `err.error.errorCode` and `err.cause`) can still find the program's
// error code.
import type { Program } from '@coral-xyz/anchor'
import { PublicKey, SystemProgram } from '@solana/web3.js'
import type { Forum } from '../idl/forum.js'
import { videoIdBytes } from './encoding.js'
import { publisherPda, containerPda, videoPda, versionRefPda, versionClaimPda } from './pda.js'

// `program.provider.publicKey` is only unset if `program` was built without a
// connected wallet — `useProgram` never returns a Program in that case, so
// reaching here is a caller bug, not a user-facing state.
function requireAuthority(program: Program<Forum>): PublicKey {
  const authority = program.provider.publicKey
  if (!authority) throw new Error('container.ts: program has no connected wallet to sign with')
  return authority
}

/** Creates a container: the append-only list of a video's published versions.
 * `containerId` is version 1's UUID (per the design's key decisions), so
 * existing links and anchors made before versioning existed keep working
 * unchanged. Must be called once, before the first `appendVersion`. */
export async function initContainer(program: Program<Forum>, containerId: string): Promise<string> {
  const authority = requireAuthority(program)
  const containerIdBytes = videoIdBytes(containerId)
  const publisher = publisherPda(authority)
  const container = containerPda(publisher, containerIdBytes)

  return program.methods
    .initContainer(Array.from(containerIdBytes))
    .accountsPartial({
      publisher,
      container,
      authority,
      systemProgram: SystemProgram.programId,
    })
    .rpc()
}

/** Appends a video to a container's version list — appending *is* publishing,
 * and the tail ref becomes the current version.
 *
 * The new `VersionRef`'s index is the container's CURRENT `version_count`,
 * read fresh off chain immediately before calling this — the PDA is seeded
 * on that number, so a cached or stale count collides with a version someone
 * else already appended and the transaction fails. Same discipline as
 * `addStorageLocation` (append.ts) reading `storage_count` fresh: a caller
 * has no index to pass in, so a stale one cannot be threaded through here. */
export async function appendVersion(program: Program<Forum>, containerId: string, videoId: string): Promise<string> {
  const authority = requireAuthority(program)
  const publisher = publisherPda(authority)
  const containerIdBytes = videoIdBytes(containerId)
  const videoIdBytesVal = videoIdBytes(videoId)
  const container = containerPda(publisher, containerIdBytes)
  const video = videoPda(publisher, videoIdBytesVal)

  const containerAccount = await program.account.videoContainer.fetch(container)
  const version = versionRefPda(container, containerAccount.versionCount)
  // One per (container, video), and the program creates it with `init` — so
  // a second append of the same video fails at account creation rather than
  // silently giving it two indices. See versionClaimPda.
  const claim = versionClaimPda(container, videoIdBytesVal)

  return program.methods
    .appendVersion(Array.from(videoIdBytesVal))
    .accountsPartial({
      publisher,
      container,
      video,
      version,
      claim,
      authority,
      systemProgram: SystemProgram.programId,
    })
    .rpc()
}
